import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateHourDb } from "../utils/tools";
import { customDateFormatType } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.bookingManagement;
const queryKey = "fetchRegisterCheckIn";

async function fetchRegisterCheckIn(
  currentPage,
  search,
  status,
  employeeId,
  saleBlockId,
  saleAreaId,
  branchId,
  startDate,
  endDate
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (employeeId) options.employeeId = employeeId;
    if (saleBlockId) options.saleBlockId = saleBlockId;
    if (saleAreaId) options.saleAreaId = saleAreaId;
    if (branchId) options.branchId = branchId;
    if (status) options.status = status;
    if (startDate) options.startTime = customDateFormatType(startDate);
    if (endDate) options.endTime = customDateFormatType(endDate);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/registerCheckIn?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        status: item?.status || "",
        code: item?.employeeObject?.code || "-",
        fullName: item?.employeeObject?.fullName || "-",
        saleBlock: item?.saleBlock?.name || "-",
        saleArea: item?.saleArea?.name || "-",
        branch: item?.branch?.name || "-",
        checkinPlace: item?.checkinPlace || "-",
        workingDayRate: item?.workingDayRate || "-",
        comment: item?.comment || "-",
        createdAt: item?.checkinTime ? formatDateHourDb(item?.checkinTime || item?.createdAt) : formatDateHourDb(item?.createdAt) || "-",

        edit: {
          id: item?._id || "",
          employeeId: item?.employeeId || "",
          checkinPlace: item?.checkinPlace || "",
          workingDayRate: item?.workingDayRate || "",
          checkinTime: item?.checkinTime || item?.createdAt ||"",
          comment: item?.comment || "",
        },
        detail: {
          id: item?._id || "",
          status: item?.status || "",
          code: item?.employeeObject?.code || "-",
          fullName: item?.employeeObject?.fullName || "-",
          saleBlock: item?.saleBlock?.name || "-",
          saleArea: item?.saleArea?.name || "-",
          branch: item?.branch?.name || "-",
          checkinPlace: item?.checkinPlace || "-",
          workingDayRate: item?.workingDayRate || "-",
          comment: item?.comment || "-",
          createdAt: item?.checkinTime ? formatDateHourDb(item?.checkinTime || item?.createdAt) : formatDateHourDb(item?.createdAt) || "-",
          confirmImage: item?.confirmImage || [],
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useRegisterCheckIn(
  search,
  status,
  employeeId,
  saleBlockId,
  saleAreaId,
  branchId,
  startDate,
  endDate
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      status,
      employeeId,
      saleBlockId,
      saleAreaId,
      branchId,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchRegisterCheckIn(
        currentPage,
        search,
        status,
        employeeId,
        saleBlockId,
        saleAreaId,
        branchId,
        startDate,
        endDate
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    registerCheckInData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
